/**
 * Phase F4 — Admin Compliance Review Center
 * ------------------------------------------
 * Surfaces Phase 2 admin compliance APIs.
 *
 * Endpoints used:
 *   GET  /api/admin/compliance/queue?status=…
 *   GET  /api/admin/compliance/{profile_id}
 *   POST /api/admin/compliance/{profile_id}/approve         (body: {review_notes?, expiration_date?, renewal_due_date?})
 *   POST /api/admin/compliance/{profile_id}/reject          (body: {review_notes})
 *   POST /api/admin/compliance/{profile_id}/request-info    (body: {review_notes})
 *
 * Pure UI. No backend logic modified.
 */
import React, { useState, useEffect, useMemo, useCallback } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import {
  ShieldCheck, Clock, CheckCircle, XCircle, AlertTriangle, FileText, X, MessageSquare,
} from 'lucide-react';
import theme from '../theme';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const TABS = [
  { key: 'COMPLIANCE_PENDING',  label: 'Pending Reviews', Icon: Clock, color: theme.colors.status.warning },
  { key: 'COMPLIANCE_APPROVED', label: 'Approved',         Icon: CheckCircle, color: theme.colors.status.success },
  { key: 'COMPLIANCE_REJECTED', label: 'Rejected',         Icon: XCircle, color: theme.colors.status.error },
  { key: 'COMPLIANCE_EXPIRED',  label: 'Expired',          Icon: AlertTriangle, color: theme.colors.text.muted },
];

const cardStyle = {
  background: theme.colors.bg.card, border: `1px solid ${theme.colors.borders}`,
  borderRadius: theme.radius.card, boxShadow: theme.colors.shadows.sm,
};

function fmtDate(iso) {
  if (!iso) return '—';
  try { return new Date(iso).toLocaleString(); } catch { return iso; }
}

export default function AdminComplianceReviewCenter() {
  const [activeTab, setActiveTab] = useState('COMPLIANCE_PENDING');
  const [rows, setRows] = useState([]);
  const [counts, setCounts] = useState({});
  const [loading, setLoading] = useState(true);
  const [drawer, setDrawer] = useState(null); // profile_id selected for review
  const [drawerData, setDrawerData] = useState(null);
  const [reviewNotes, setReviewNotes] = useState('');
  const [busy, setBusy] = useState(false);

  const auth = useMemo(() => ({
    headers: { Authorization: `Bearer ${localStorage.getItem('token')}` },
  }), []);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/admin/compliance/queue?status=${activeTab}`, auth);
      setRows(res.data?.profiles || []);
      setCounts(res.data?.bucket_counts || {});
    } catch (e) {
      toast.error('Failed to load compliance queue');
    } finally {
      setLoading(false);
    }
  }, [activeTab, auth]);

  useEffect(() => { load(); }, [load]);

  const openDrawer = async (id) => {
    setDrawer(id); setDrawerData(null); setReviewNotes('');
    try {
      const res = await axios.get(`${API}/admin/compliance/${id}`, auth);
      setDrawerData(res.data?.profile || null);
    } catch (e) {
      toast.error('Failed to load profile');
    }
  };

  const decide = async (action) => {
    if (!drawer) return;
    if ((action === 'reject' || action === 'request-info') && !reviewNotes.trim()) {
      toast.error('Notes required for this action.');
      return;
    }
    setBusy(true);
    try {
      await axios.post(`${API}/admin/compliance/${drawer}/${action}`, { review_notes: reviewNotes || undefined }, auth);
      toast.success(`Profile ${action.replace('-', ' ')}`);
      setDrawer(null);
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || `${action} failed`);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div data-testid="admin-compliance-review-page" style={{ padding: 24, maxWidth: 1300, margin: '0 auto' }}>
      <div style={{ marginBottom: 18 }}>
        <h1 style={{
          fontSize: 28, fontWeight: 800, color: theme.colors.text.primary, margin: 0,
          display: 'flex', alignItems: 'center', gap: 10,
        }}>
          <ShieldCheck size={26} color={theme.colors.primary} />
          Compliance Review Center
        </h1>
        <p style={{ color: theme.colors.text.secondary, marginTop: 4 }}>
          Review submitted profiles, approve, reject, or request additional information.
        </p>
      </div>

      <div data-testid="compliance-tabs" style={{ display: 'flex', gap: 8, marginBottom: 18, flexWrap: 'wrap' }}>
        {TABS.map((t) => {
          const Icon = t.Icon;
          const active = activeTab === t.key;
          return (
            <button
              key={t.key}
              data-testid={`compliance-tab-${t.key}`}
              onClick={() => setActiveTab(t.key)}
              style={{
                display: 'inline-flex', alignItems: 'center', gap: 8,
                padding: '10px 16px', borderRadius: theme.radius.cta,
                border: `1px solid ${active ? t.color : theme.colors.borders}`,
                background: active ? `${t.color}15` : theme.colors.bg.card,
                color: active ? t.color : theme.colors.text.secondary,
                cursor: 'pointer', fontWeight: 600, fontSize: 13,
              }}
            >
              <Icon size={16} /> {t.label}
              <span style={{
                marginLeft: 4, background: active ? t.color : theme.colors.bg.tertiary,
                color: active ? '#fff' : theme.colors.text.secondary,
                padding: '1px 8px', borderRadius: 999, fontSize: 11, fontWeight: 700,
              }}>{counts[t.key] || 0}</span>
            </button>
          );
        })}
      </div>

      {loading ? (
        <div data-testid="compliance-loading" style={{ ...cardStyle, padding: 40, textAlign: 'center', color: theme.colors.text.muted }}>
          Loading…
        </div>
      ) : rows.length === 0 ? (
        <div data-testid="compliance-empty" style={{ ...cardStyle, padding: 40, textAlign: 'center', color: theme.colors.text.secondary }}>
          No profiles in this bucket.
        </div>
      ) : (
        <div style={{ ...cardStyle, padding: 0, overflow: 'hidden' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
            <thead style={{ background: theme.colors.bg.secondary }}>
              <tr>
                {['User', 'Role', 'Legal Name', 'Country', 'Submitted', 'Status', ''].map((h) => (
                  <th key={h} style={{
                    textAlign: 'left', padding: '10px 12px',
                    fontSize: 11, textTransform: 'uppercase', letterSpacing: 0.5,
                    color: theme.colors.text.muted, fontWeight: 700,
                  }}>{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {rows.map((r) => (
                <tr key={r.id} data-testid={`compliance-row-${r.id}`} style={{ borderTop: `1px solid ${theme.colors.borders}` }}>
                  <td style={{ padding: '10px 12px', fontFamily: 'monospace', fontSize: 12, color: theme.colors.text.secondary }}>
                    {(r.user_id || '').slice(0, 8)}…
                  </td>
                  <td style={{ padding: '10px 12px' }}>{r.role}</td>
                  <td style={{ padding: '10px 12px', fontWeight: 600, color: theme.colors.text.primary }}>
                    {r.legal_name || r.company_name || '—'}
                  </td>
                  <td style={{ padding: '10px 12px' }}>{r.country || '—'}</td>
                  <td style={{ padding: '10px 12px', color: theme.colors.text.secondary }}>{fmtDate(r.submitted_at)}</td>
                  <td style={{ padding: '10px 12px' }}>
                    <span style={{
                      padding: '2px 8px', borderRadius: 999,
                      background: `${theme.colors.primary}15`, color: theme.colors.primary,
                      fontSize: 11, fontWeight: 700,
                    }}>{r.status}</span>
                  </td>
                  <td style={{ padding: '10px 12px' }}>
                    <button
                      data-testid={`compliance-review-${r.id}`}
                      onClick={() => openDrawer(r.id)}
                      style={{
                        padding: '6px 12px', borderRadius: theme.radius.sm, cursor: 'pointer',
                        border: 'none', background: theme.colors.primary, color: '#fff',
                        fontSize: 12, fontWeight: 700,
                      }}
                    >
                      Review
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Side drawer */}
      {drawer && (
        <div
          data-testid="compliance-drawer"
          style={{
            position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.4)',
            display: 'flex', justifyContent: 'flex-end', zIndex: 100,
          }}
          onClick={() => setDrawer(null)}
        >
          <div
            onClick={(e) => e.stopPropagation()}
            style={{
              width: 'min(560px, 100%)', height: '100%', background: theme.colors.bg.card,
              padding: 24, overflowY: 'auto', boxShadow: theme.colors.shadows.lg,
            }}
          >
            <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 16 }}>
              <div style={{ fontWeight: 800, fontSize: 18, color: theme.colors.text.primary }}>
                Compliance Profile
              </div>
              <button onClick={() => setDrawer(null)} style={{
                background: 'none', border: 'none', cursor: 'pointer', color: theme.colors.text.muted,
              }}><X size={20} /></button>
            </div>

            {!drawerData ? (
              <div style={{ color: theme.colors.text.muted }}>Loading…</div>
            ) : (
              <>
                <div style={{ marginBottom: 14, fontSize: 13 }}>
                  {[
                    ['Status', drawerData.status],
                    ['Role', drawerData.role],
                    ['Legal Name', drawerData.legal_name],
                    ['Company', drawerData.company_name],
                    ['Country', drawerData.country],
                    ['KYC', drawerData.kyc_status],
                    ['Submitted', fmtDate(drawerData.submitted_at)],
                    ['Approved', fmtDate(drawerData.approved_at)],
                    ['Expires', fmtDate(drawerData.expiration_date)],
                    ['Renewal', drawerData.renewal_status],
                  ].map(([k, v]) => (
                    <div key={k} style={{ display: 'flex', justifyContent: 'space-between', padding: '6px 0', borderBottom: `1px solid ${theme.colors.borders}` }}>
                      <span style={{ color: theme.colors.text.muted }}>{k}</span>
                      <span style={{ fontWeight: 600, color: theme.colors.text.primary }}>{v || '—'}</span>
                    </div>
                  ))}
                </div>

                {(drawerData.credentials || []).length > 0 && (
                  <div style={{ marginBottom: 14 }}>
                    <div style={{ fontWeight: 700, marginBottom: 6, fontSize: 13, color: theme.colors.text.primary }}>
                      Credentials
                    </div>
                    {drawerData.credentials.map((c, i) => (
                      <div key={i} style={{
                        padding: 8, background: theme.colors.bg.secondary,
                        borderRadius: theme.radius.sm, marginBottom: 6, fontSize: 12,
                      }}>
                        <div style={{ fontWeight: 600 }}>{c.license_type} · {c.license_number}</div>
                        <div style={{ color: theme.colors.text.secondary }}>
                          {c.issuing_authority} · {c.verification_status}
                        </div>
                      </div>
                    ))}
                  </div>
                )}

                <div style={{ marginTop: 12 }}>
                  <label style={{ display: 'block', fontSize: 12, fontWeight: 600, color: theme.colors.text.secondary, marginBottom: 6 }}>
                    Review Notes
                  </label>
                  <textarea
                    data-testid="compliance-review-notes"
                    value={reviewNotes}
                    onChange={(e) => setReviewNotes(e.target.value)}
                    placeholder="Notes (required for reject / request info)"
                    style={{
                      width: '100%', minHeight: 90, padding: 10,
                      border: `1px solid ${theme.colors.borders}`,
                      borderRadius: theme.radius.sm, fontFamily: 'inherit', fontSize: 13,
                      outline: 'none', boxSizing: 'border-box', marginBottom: 12,
                    }}
                  />
                  <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                    <button
                      data-testid="compliance-approve-btn"
                      disabled={busy}
                      onClick={() => decide('approve')}
                      style={{
                        padding: '8px 14px', borderRadius: theme.radius.sm,
                        border: 'none', background: theme.colors.status.success,
                        color: '#fff', cursor: 'pointer', fontWeight: 700, fontSize: 13,
                        display: 'inline-flex', alignItems: 'center', gap: 6,
                      }}
                    >
                      <CheckCircle size={14} /> Approve
                    </button>
                    <button
                      data-testid="compliance-reject-btn"
                      disabled={busy}
                      onClick={() => decide('reject')}
                      style={{
                        padding: '8px 14px', borderRadius: theme.radius.sm,
                        border: `1px solid ${theme.colors.borders}`,
                        background: theme.colors.bg.card, color: theme.colors.status.error,
                        cursor: 'pointer', fontWeight: 700, fontSize: 13,
                        display: 'inline-flex', alignItems: 'center', gap: 6,
                      }}
                    >
                      <XCircle size={14} /> Reject
                    </button>
                    <button
                      data-testid="compliance-request-info-btn"
                      disabled={busy}
                      onClick={() => decide('request-info')}
                      style={{
                        padding: '8px 14px', borderRadius: theme.radius.sm,
                        border: `1px solid ${theme.colors.borders}`,
                        background: theme.colors.bg.card, color: theme.colors.status.warning,
                        cursor: 'pointer', fontWeight: 700, fontSize: 13,
                        display: 'inline-flex', alignItems: 'center', gap: 6,
                      }}
                    >
                      <MessageSquare size={14} /> Request Info
                    </button>
                  </div>
                </div>

                {(drawerData.approval_history || []).length > 0 && (
                  <div style={{ marginTop: 18 }}>
                    <div style={{ fontWeight: 700, marginBottom: 6, fontSize: 13 }}>History</div>
                    {drawerData.approval_history.map((h, i) => (
                      <div key={i} style={{ padding: 8, background: theme.colors.bg.secondary, marginBottom: 6, borderRadius: theme.radius.sm, fontSize: 12 }}>
                        <div style={{ fontWeight: 600 }}>{h.action} · {h.actor_role}</div>
                        <div style={{ color: theme.colors.text.secondary }}>{fmtDate(h.date)}</div>
                        {h.notes && <div style={{ marginTop: 4, color: theme.colors.text.muted }}>{h.notes}</div>}
                      </div>
                    ))}
                  </div>
                )}
              </>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
